import React, { useState, useEffect, useMemo } from 'react';
import { Users, Trash2, QrCode, CheckCircle, X } from 'lucide-react';
import FilterBar from './FilterBar';
import useAutoRefresh from '../../lib/useAutoRefresh';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Visitors() {
  const [tab, setTab] = useState('master');
  const [visitors, setVisitors] = useState([]);
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateRange, setDateRange] = useState({ from: '', to: '' });
  const [closing, setClosing] = useState(null);     // visit obj currently being closed
  const [closeRemarks, setCloseRemarks] = useState('');
  const [closeBusy, setCloseBusy] = useState(false);

  const filteredVisitors = useMemo(() => {
    const q = search.trim().toLowerCase();
    return visitors.filter(v => {
      if (statusFilter !== 'all' && v.status !== statusFilter) return false;
      if (q && !(
        (v.name || '').toLowerCase().includes(q) ||
        (v.mobile || '').toLowerCase().includes(q) ||
        (v.company || '').toLowerCase().includes(q) ||
        (v.designation || '').toLowerCase().includes(q)
      )) return false;
      return true;
    });
  }, [visitors, search, statusFilter]);

  const filteredVisits = useMemo(() => {
    const q = search.trim().toLowerCase();
    return visits.filter(v => {
      if (statusFilter !== 'all' && v.status !== statusFilter) return false;
      if (dateRange.from || dateRange.to) {
        const t = v.in_approved_at || v.in_request_at || v.created_at;
        if (!t) return false;
        const day = new Date(t).toISOString().slice(0, 10);
        if (dateRange.from && day < dateRange.from) return false;
        if (dateRange.to && day > dateRange.to) return false;
      }
      if (q && !(
        (v.visitor_name || '').toLowerCase().includes(q) ||
        (v.visitor_company || '').toLowerCase().includes(q) ||
        (v.staff_name || '').toLowerCase().includes(q) ||
        (v.entry_code || '').toLowerCase().includes(q) ||
        (v.purpose || '').toLowerCase().includes(q)
      )) return false;
      return true;
    });
  }, [visits, search, statusFilter, dateRange]);

  const visitorUrl = `${window.location.origin}/visitor`;

  useEffect(() => {
    if (tab === 'master') fetchVisitors();
    else fetchVisits();
  }, [tab]);

  // Silent auto-refresh every 30 s — re-runs whichever fetcher matches the active tab.
  useAutoRefresh(async () => {
    try {
      const token = localStorage.getItem('token');
      const url = tab === 'master' ? '/api/admin/visitors' : '/api/admin/visitor-visits';
      const r = await fetch(`${API_URL}${url}`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (!r.ok) return;
      const d = await r.json();
      if (tab === 'master') setVisitors(d || []);
      else setVisits(d || []);
    } catch {}
  }, 30_000);

  const fetchVisitors = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_URL}/api/admin/visitors`, { headers: { 'Authorization': `Bearer ${token}` } });
      const d = await r.json();
      setVisitors(d || []);
    } finally { setLoading(false); }
  };

  const fetchVisits = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_URL}/api/admin/visitor-visits`, { headers: { 'Authorization': `Bearer ${token}` } });
      const d = await r.json();
      setVisits(d || []);
    } finally { setLoading(false); }
  };

  const deleteVisitor = async (v) => {
    if (!window.confirm(`Delete visitor ${v.name}?`)) return;
    const token = localStorage.getItem('token');
    const r = await fetch(`${API_URL}/api/admin/visitors/${v.id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (r.ok) fetchVisitors();
  };

  const submitAdminMeetingDone = async () => {
    if (!closing) return;
    setCloseBusy(true);
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_URL}/api/visitors/${closing.id}/meeting-done`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ remarks: closeRemarks }),
      });
      const d = await r.json();
      if (r.ok) {
        const flow = d.gate_required ? `visit moved to "meeting done" — visitor will press OUT for gate to approve.` : `visit auto-completed (gate OFF).`;
        alert(`Exit OTP ${d.exit_otp} sent on WhatsApp. ${flow}`);
        setClosing(null);
        setCloseRemarks('');
        fetchVisits();
      } else {
        alert(d.detail || 'Failed to close meeting');
      }
    } finally { setCloseBusy(false); }
  };

  const fmt = (iso) => iso ? new Date(iso).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' }) : '-';

  return (
    <div data-testid="visitors-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h1 className="text-2xl font-bold text-gray-900">Visitors</h1>
        <a
          href={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(visitorUrl)}`}
          target="_blank" rel="noreferrer"
          className="flex items-center space-x-2 px-3 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-sm"
          data-testid="visitor-qr-btn"
        >
          <QrCode size={16} /><span>Print QR</span>
        </a>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-sm">
        <p className="text-amber-900 font-medium">Visitor self-service URL:</p>
        <a href={visitorUrl} target="_blank" rel="noreferrer" className="text-blue-600 underline break-all">{visitorUrl}</a>
        <p className="text-xs text-amber-700 mt-1">Print this QR at the reception. Visitors scan to register / start a visit.</p>
      </div>

      <div className="flex gap-2 mb-4">
        <button onClick={() => setTab('master')} className={`px-3 py-2 rounded-lg text-sm font-medium border ${tab === 'master' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white border-gray-200'}`} data-testid="tab-master">
          <Users size={14} className="inline mr-1" />Visitor Master ({visitors.length})
        </button>
        <button onClick={() => setTab('log')} className={`px-3 py-2 rounded-lg text-sm font-medium border ${tab === 'log' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white border-gray-200'}`} data-testid="tab-log">
          Visit Log ({visits.length})
        </button>
      </div>

      {loading && <div className="text-center py-8">Loading...</div>}

      {!loading && (
        <FilterBar
          testId="visitors-filters"
          placeholder={tab === 'master' ? 'Search by name, mobile, company…' : 'Search by visitor, staff, code, purpose…'}
          search={search}
          onSearch={setSearch}
          chips={tab === 'master' ? [
            { key: 'status', label: 'Status', value: statusFilter, options: [
              { value: 'registered', label: 'Registered' }, { value: 'pending_otp', label: 'Pending OTP' }
            ]}
          ] : [
            { key: 'status', label: 'Status', value: statusFilter, options: [
              { value: 'pending_in', label: 'Pending In' },
              { value: 'inside', label: 'Inside' },
              { value: 'meeting_done', label: 'Meeting Done' },
              { value: 'completed', label: 'Completed' }
            ]}
          ]}
          onChipChange={(_, v) => setStatusFilter(v)}
          dateRange={tab === 'log' ? dateRange : null}
          onDateChange={setDateRange}
          onClear={() => { setSearch(''); setStatusFilter('all'); setDateRange({ from: '', to: '' }); }}
          resultCount={tab === 'master' ? filteredVisitors.length : filteredVisits.length}
        />
      )}

      {!loading && tab === 'master' && (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Photo</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mobile</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Designation</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Registered</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredVisitors.map(v => (
                  <tr key={v.id} className="hover:bg-gray-50" data-testid={`visitor-row-${v.id}`}>
                    <td className="px-4 py-2">
                      {v.photo_base64 ? <img src={v.photo_base64} alt="" className="w-10 h-10 rounded-full object-cover" /> : <span className="text-xs text-gray-400">—</span>}
                    </td>
                    <td className="px-4 py-2 font-medium">{v.name}</td>
                    <td className="px-4 py-2 text-gray-600">{v.mobile}</td>
                    <td className="px-4 py-2 text-gray-600">{v.company}</td>
                    <td className="px-4 py-2 text-gray-600 text-xs">{v.designation}</td>
                    <td className="px-4 py-2">
                      <span className={`px-2 py-0.5 text-xs rounded-full ${v.status === 'registered' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>{v.status}</span>
                    </td>
                    <td className="px-4 py-2 text-xs text-gray-600">{fmt(v.registered_at || v.created_at)}</td>
                    <td className="px-4 py-2 text-right">
                      <button onClick={() => deleteVisitor(v)} className="text-red-600 p-1" data-testid={`delete-visitor-${v.id}`} title="Delete"><Trash2 size={16} /></button>
                    </td>
                  </tr>
                ))}
                {filteredVisitors.length === 0 && (
                  <tr><td colSpan="8" className="px-4 py-8 text-center text-gray-500">{visitors.length === 0 ? 'No visitors registered yet' : 'No visitors match the current filter'}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!loading && tab === 'log' && (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Visitor</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Met</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Purpose</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">In</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Out</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mins</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Staff Remarks</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-amber-700 uppercase">Visitor Remarks (admin only)</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredVisits.map(v => (
                  <tr key={v.id} className="hover:bg-gray-50" data-testid={`visit-row-${v.id}`}>
                    <td className="px-4 py-2 font-mono font-bold text-amber-700">{v.entry_code}</td>
                    <td className="px-4 py-2">
                      <p className="font-medium">{v.visitor_name}</p>
                      <p className="text-xs text-gray-500">{v.visitor_company}</p>
                    </td>
                    <td className="px-4 py-2 text-gray-700">{v.staff_name}</td>
                    <td className="px-4 py-2 text-xs text-gray-600">{v.purpose || '-'}</td>
                    <td className="px-4 py-2 text-xs">{fmt(v.in_approved_at || v.in_request_at)}</td>
                    <td className="px-4 py-2 text-xs">{fmt(v.out_approved_at)}</td>
                    <td className="px-4 py-2">{v.duration_minutes ? `${v.duration_minutes} min` : '-'}</td>
                    <td className="px-4 py-2">
                      <span className={`px-2 py-0.5 text-xs rounded-full capitalize ${
                        v.status === 'completed' ? 'bg-gray-100 text-gray-700' :
                        v.status === 'inside' ? 'bg-blue-100 text-blue-700' :
                        v.status === 'meeting_done' ? 'bg-green-100 text-green-700' :
                        'bg-orange-100 text-orange-700'
                      }`}>{v.status.replace('_', ' ')}</span>
                      {v.meeting_done_by_role === 'admin' && (
                        <span className="ml-1 text-[10px] text-purple-700 font-semibold uppercase">admin-closed</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-xs text-gray-600 max-w-xs truncate">{v.meeting_remarks || '-'}</td>
                    <td className="px-4 py-2 text-xs text-amber-800 font-medium max-w-xs truncate" data-testid={`visitor-remarks-${v.id}`}>
                      {v.visitor_remarks || '-'}
                    </td>
                    <td className="px-4 py-2 text-right">
                      {(v.status === 'inside' || v.status === 'pending_in') && (
                        <button
                          onClick={() => { setClosing(v); setCloseRemarks(''); }}
                          className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-xs font-semibold"
                          data-testid={`admin-meeting-done-${v.id}`}
                          title="Mark Meeting Over (admin override) — issues exit OTP for the visitor"
                        >
                          <CheckCircle size={12} /> Meeting Over
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {filteredVisits.length === 0 && (
                  <tr><td colSpan="11" className="px-4 py-8 text-center text-gray-500">{visits.length === 0 ? 'No visitor visits yet' : 'No records match the current filter'}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {closing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="admin-meeting-done-modal">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-5">
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="font-semibold text-lg">Mark Meeting Over</h3>
                <p className="text-xs text-gray-500">Admin override — issues the visitor's exit OTP.</p>
              </div>
              <button onClick={() => { setClosing(null); setCloseRemarks(''); }} className="p-1 rounded hover:bg-gray-100" data-testid="admin-meeting-done-cancel">
                <X size={18} />
              </button>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5 mb-3 text-xs text-amber-900">
              <p><strong>{closing.visitor_name}</strong> · {closing.visitor_company || '—'}</p>
              <p>Originally to meet: <strong>{closing.staff_name}</strong></p>
              <p>Status: <span className="capitalize font-semibold">{closing.status.replace('_', ' ')}</span></p>
            </div>
            <label className="text-xs font-medium text-gray-700">Remarks (optional)</label>
            <textarea
              rows={3}
              maxLength={200}
              placeholder="e.g. Host on leave — admin closed the meeting."
              value={closeRemarks}
              onChange={(e) => setCloseRemarks(e.target.value)}
              className="w-full mt-1 px-3 py-2 border-2 border-gray-200 rounded-lg outline-none focus:border-emerald-400 text-sm"
              data-testid="admin-meeting-done-remarks"
            />
            <p className="text-[10px] text-gray-400 text-right">{closeRemarks.length}/200</p>
            <button
              onClick={submitAdminMeetingDone}
              disabled={closeBusy}
              className="mt-2 w-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-bold py-2.5 rounded-lg flex items-center justify-center gap-2"
              data-testid="admin-meeting-done-submit"
            >
              <CheckCircle size={16} /> {closeBusy ? 'Sending exit OTP…' : 'Confirm — Issue Exit OTP'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
